import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { checkAccount, loginUser, registerUser } from '../bridge';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const logo = require('../logo.jpeg');

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // If redirected from a protected page, go back there after login
  const from = location.state?.from || '/manage';

  const [tab, setTab] = useState(location.state?.tab === 'signup' ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form state
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateEmail(loginEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (loginPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    const user = await loginUser(loginEmail.trim().toLowerCase(), loginPassword);
    setLoading(false);
    if (!user) {
      setError('Incorrect email or password. Please try again.');
      return;
    }
    login(user.email);
    navigate(from, { replace: true });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateEmail(regEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (regPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!termsAccepted) {
      setError('Please accept the Terms & Conditions to continue.');
      return;
    }
    setLoading(true);
    const exists = await checkAccount(regEmail.trim().toLowerCase());
    if (exists) {
      setLoading(false);
      setError('An account with this email already exists. Please log in.');
      return;
    }
    const ok = await registerUser(regEmail.trim().toLowerCase(), regPassword);
    setLoading(false);
    if (!ok) {
      setError('Something went wrong. Please try again.');
      return;
    }
    navigate('/register/security-questions', {
      state: { email: regEmail.trim().toLowerCase(), mode: 'register' },
    });
  };

  return (
    <div className="auth-page">
      <nav className="navbar">
        <div className="navbar-inner">
          <Link to="/" className="navbar-brand">
            <img src={logo} className="navbar-logo" alt="Menu & Catalog Maker" />
            <span className="navbar-title">Menu &amp; Catalog Maker</span>
          </Link>
        </div>
      </nav>

      <div className="auth-body">
        <div className="auth-card">
          <div className="auth-tabs">
            <button
              className={`auth-tab${tab === 0 ? ' active' : ''}`}
              onClick={() => { setTab(0); setError(''); }}
            >
              Log in
            </button>
            <button
              className={`auth-tab${tab === 1 ? ' active' : ''}`}
              onClick={() => { setTab(1); setError(''); }}
            >
              Create account
            </button>
          </div>

          {error && <div className="auth-error">{error}</div>}

          {tab === 0 ? (
            <form onSubmit={handleLogin} noValidate>
              <div className="auth-field">
                <label className="auth-label">Email</label>
                <input
                  className="auth-input"
                  type="email"
                  placeholder="you@example.com"
                  value={loginEmail}
                  onChange={e => setLoginEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input
                  className="auth-input"
                  type="password"
                  placeholder="••••••••"
                  value={loginPassword}
                  onChange={e => setLoginPassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              <div className="auth-link-row" style={{ textAlign: 'right', marginBottom: 20 }}>
                <Link to="/account-recovery" className="auth-link">
                  Forgot password?
                </Link>
              </div>
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? 'Logging in…' : 'Log in'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} noValidate>
              <div className="auth-field">
                <label className="auth-label">Email</label>
                <input
                  className="auth-input"
                  type="email"
                  placeholder="you@example.com"
                  value={regEmail}
                  onChange={e => setRegEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <div className="auth-field">
                <label className="auth-label">Password</label>
                <input
                  className="auth-input"
                  type="password"
                  placeholder="Min. 8 characters"
                  value={regPassword}
                  onChange={e => setRegPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <div className="auth-checkbox-row">
                <input
                  id="terms"
                  className="auth-checkbox"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                />
                <label htmlFor="terms" className="auth-checkbox-label">
                  I agree to the{' '}
                  <Link to="/privacypolicy">Terms &amp; Conditions</Link>
                </label>
              </div>
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? 'Creating account…' : 'Create account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
